/**
 * How much of a thing is in the house.
 *
 * `sizeText` already exists and is the verbatim string off a label ("1 L",
 * "200 g"). It is a READING, so it must stay exactly as printed — it is
 * evidence. This is the parsed, editable, arithmetic-capable version, and the
 * two are deliberately separate: correcting the amount you have must never
 * rewrite what the packet says.
 *
 * The unit set is closed and small on purpose. Every unit here is one an Indian
 * kitchen actually buys in, and a closed set is what lets the UI offer a
 * sensible step button and the model be scored against a fixed vocabulary.
 */
export const UNITS = [
    "l",
    "ml",
    "kg",
    "g",
    "piece",
    "pack",
    "dozen",
    "bunch",
];
export const UNIT_DIMENSION = {
    l: "volume",
    ml: "volume",
    kg: "mass",
    g: "mass",
    piece: "count",
    pack: "count",
    dozen: "count",
    bunch: "count",
};
/** Singular display form. `formatQuantity` handles the plural. */
export const UNIT_LABEL = {
    l: "L",
    ml: "ml",
    kg: "kg",
    g: "g",
    piece: "piece",
    pack: "pack",
    dozen: "dozen",
    bunch: "bunch",
};
/** Units that read as a word and therefore pluralise. "2 L", but "2 packs". */
const PLURALISES = new Set(["piece", "pack", "bunch"]);
/**
 * How much one tap of +/- should move.
 *
 * Chosen so the common case is one tap, not five: milk moves in half litres
 * because that is how it is sold, spices move in 50 g because a 500 g jar of
 * jeera does not exist. Getting this wrong turns a stepper into a chore.
 */
export const UNIT_STEP = {
    l: 0.5,
    ml: 100,
    kg: 0.5,
    g: 50,
    piece: 1,
    pack: 1,
    dozen: 1,
    bunch: 1,
};
/** Nobody has 400 litres of anything, and a typo should not claim they do. */
export const MAX_AMOUNT = 999;
/**
 * Base units for comparison: millilitres, grams, or a bare count.
 *
 * Only ever compare within a dimension — `UNIT_DIMENSION` is what tells you
 * whether the comparison is meaningful at all.
 */
export function toBase(q) {
    switch (q.unit) {
        case "l":
            return q.amount * 1000;
        case "kg":
            return q.amount * 1000;
        case "dozen":
            return q.amount * 12;
        default:
            return q.amount;
    }
}
/**
 * Trims float noise without lying about precision.
 *
 * 0.1 + 0.2 must display as "0.3", not "0.30000000000000004", and 1.0 must
 * display as "1" rather than "1.0". Two decimals is as fine as any kitchen
 * quantity gets.
 */
function tidy(n) {
    return Math.round(n * 100) / 100;
}
/** "1 L", "500 g", "2 packs", "1.5 kg". */
export function formatQuantity(q) {
    const n = tidy(q.amount);
    const label = UNIT_LABEL[q.unit];
    const plural = n !== 1 && PLURALISES.has(q.unit) ? "s" : "";
    return `${n} ${label}${plural}`;
}
/** Clamps an amount into the range the UI and the database both accept. */
export function clampAmount(n) {
    if (!Number.isFinite(n) || n <= 0)
        return 1;
    return tidy(Math.min(MAX_AMOUNT, n));
}
/** Applies one step of a stepper, never going to zero or below. */
export function stepQuantity(q, direction) {
    const step = UNIT_STEP[q.unit];
    const next = tidy(q.amount + step * direction);
    // A stepper must never reach 0: "0 kg of atta" is not a thing you own, it is
    // a thing you have run out of, and that is an event, not a quantity.
    return { ...q, amount: next <= 0 ? step : clampAmount(next) };
}
/**
 * Spellings that reach us from labels, speech and typing, mapped to the closed
 * set. Speech recognition especially produces "litre" and "kilo" as words.
 */
const UNIT_ALIASES = {
    l: "l",
    lt: "l",
    ltr: "l",
    litre: "l",
    litres: "l",
    liter: "l",
    liters: "l",
    ml: "ml",
    millilitre: "ml",
    millilitres: "ml",
    milliliter: "ml",
    milliliters: "ml",
    cc: "ml",
    kg: "kg",
    kgs: "kg",
    kilo: "kg",
    kilos: "kg",
    kilogram: "kg",
    kilograms: "kg",
    g: "g",
    gm: "g",
    gms: "g",
    gram: "g",
    grams: "g",
    gramme: "g",
    grammes: "g",
    piece: "piece",
    pieces: "piece",
    pc: "piece",
    pcs: "piece",
    no: "piece",
    nos: "piece",
    unit: "piece",
    units: "piece",
    egg: "piece",
    eggs: "piece",
    pack: "pack",
    packs: "pack",
    packet: "pack",
    packets: "pack",
    pkt: "pack",
    pouch: "pack",
    pouches: "pack",
    box: "pack",
    boxes: "pack",
    tin: "pack",
    tins: "pack",
    can: "pack",
    cans: "pack",
    jar: "pack",
    jars: "pack",
    bottle: "pack",
    bottles: "pack",
    bag: "pack",
    bags: "pack",
    sachet: "pack",
    sachets: "pack",
    dozen: "dozen",
    dozens: "dozen",
    doz: "dozen",
    bunch: "bunch",
    bunches: "bunch",
    bundle: "bunch",
    bundles: "bunch",
};
/** Is this a unit word we understand? Exported for the model's coercion layer. */
export function toUnit(raw) {
    if (typeof raw !== "string")
        return null;
    return UNIT_ALIASES[raw.trim().toLowerCase()] ?? null;
}
/**
 * Reads a quantity out of free text.
 *
 * Deterministic and free — it runs BEFORE any model call, because "1 L" needs
 * no intelligence to understand and paying a model to read it would be both
 * slower and less reliable than a regex.
 *
 * Handles the multipack form too: "2 x 100 g" is 200 g, because what you own is
 * 200 g of the stuff. The pack count is a different fact and lives in `qty`.
 */
export function parseQuantity(text) {
    if (!text)
        return null;
    const s = text.toLowerCase().replace(/,/g, "");
    // "2 x 100 g" / "2*100ml" — multiply through to the total.
    const multi = s.match(/(\d+(?:\.\d+)?)\s*[x*×]\s*(\d+(?:\.\d+)?)\s*([a-z]+)/);
    if (multi) {
        const unit = toUnit(multi[3]);
        if (unit) {
            const total = Number(multi[1]) * Number(multi[2]);
            if (Number.isFinite(total) && total > 0) {
                return { amount: clampAmount(total), unit };
            }
        }
    }
    // The ordinary "<number> <unit>" form, anywhere in the string.
    const one = s.match(/(\d+(?:\.\d+)?)\s*([a-z]+)/);
    if (one) {
        const unit = toUnit(one[2]);
        const amount = Number(one[1]);
        if (unit && Number.isFinite(amount) && amount > 0) {
            return { amount: clampAmount(amount), unit };
        }
    }
    // "a dozen eggs", "a kilo of atta" — a bare unit with no number means one.
    const bare = s.match(/\b([a-z]+)\b/g) ?? [];
    for (const word of bare) {
        const unit = toUnit(word);
        // Only the units that make sense unnumbered. "milk g" must not become 1 g.
        if (unit && (unit === "dozen" || unit === "bunch" || unit === "pack")) {
            return { amount: 1, unit };
        }
    }
    return null;
}
/**
 * What a thing is usually bought in, guessed from its name alone.
 *
 * Free, instant and offline — which is the entire point. Enrichment knows more
 * but arrives seconds AFTER a save, and the add screen needs something to
 * suggest while the user is still standing there. Milk is sold in litres and
 * atta in kilos in every shop in India; that does not need a model.
 *
 * This is a SUGGESTION and the UI must present it as one. It is matched on
 * whole words so "oil" does not fire on "boiled", and it deliberately covers
 * only the short head of the distribution — the tail is what enrichment is for,
 * and a wrong guess here would be a wrong number sitting in a field the user
 * has to notice and correct.
 */
const TYPICAL = [
    {
        words: ["milk", "doodh", "curd", "dahi", "buttermilk", "lassi"],
        q: { amount: 1, unit: "l" },
    },
    { words: ["oil", "tel", "ghee", "vinegar"], q: { amount: 1, unit: "l" } },
    { words: ["water", "juice", "cola", "soda"], q: { amount: 1, unit: "l" } },
    {
        words: [
            "rice",
            "chawal",
            "atta",
            "flour",
            "maida",
            "sugar",
            "cheeni",
            "salt",
            "namak",
            "dal",
            "daal",
            "rajma",
            "chana",
            "poha",
            "suji",
            "rava",
            "besan",
            "onion",
            "potato",
            "tomato",
            "aloo",
            "pyaz",
        ],
        q: { amount: 1, unit: "kg" },
    },
    { words: ["paneer", "butter", "cheese", "khoya"], q: { amount: 200, unit: "g" } },
    {
        words: ["tea", "chai", "coffee", "masala", "haldi", "jeera", "mirch"],
        q: { amount: 100, unit: "g" },
    },
    {
        words: [
            "coriander",
            "dhania",
            "methi",
            "palak",
            "mint",
            "pudina",
            "spinach",
            "curry",
        ],
        q: { amount: 1, unit: "bunch" },
    },
    { words: ["egg", "eggs", "anda"], q: { amount: 6, unit: "piece" } },
    {
        words: ["banana", "orange", "lemon", "nimbu", "coconut"],
        q: { amount: 6, unit: "piece" },
    },
    {
        words: ["bread", "biscuit", "biscuits", "chips", "namkeen", "maggi", "noodles"],
        q: { amount: 1, unit: "pack" },
    },
];
/**
 * A likely quantity for a name, or null when we genuinely have no idea.
 *
 * Null is a perfectly good answer and the common one. Never returns a default
 * "1 piece" — a made-up unit shown in a field reads as a fact, and the user
 * would have to notice it was wrong rather than simply filling in a blank.
 */
export function guessQuantity(name) {
    const words = new Set(name
        .toLowerCase()
        .replace(/[^\p{L}\p{M}\p{N}\s]/gu, " ")
        .split(/\s+/)
        .filter(Boolean));
    if (words.size === 0)
        return null;
    for (const row of TYPICAL) {
        for (const w of row.words) {
            if (words.has(w))
                return { ...row.q };
        }
    }
    return null;
}
/**
 * Coerces whatever a model returned into a Quantity, or null.
 *
 * Same posture as the extraction layer: assume nothing. A malformed amount must
 * become null rather than a plausible-looking number, because the whole point of
 * showing a quantity is that the user can trust it enough to only glance at it.
 */
export function coerceQuantity(rawAmount, rawUnit) {
    const unit = toUnit(rawUnit);
    if (!unit)
        return null;
    const amount = Number(rawAmount);
    if (!Number.isFinite(amount) || amount <= 0)
        return null;
    return { amount: clampAmount(amount), unit };
}
//# sourceMappingURL=quantity.js.map